/**
 * ============================================================================
 * BOM管理模块 API 工厂函数
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义 BOM管理模块的所有 API 请求方法
 * - 通过工厂模式实现请求客户端的依赖注入
 * - 提供完整的 TypeScript 类型安全
 * 
 * 🎯 核心概念：工厂模式
 * - 工厂函数：createBomApi(client) - 接收请求客户端，返回 API 方法集合
 * - 依赖注入：不依赖具体的请求实现（axios、uni.request 等）
 * - 平台无关：Web 端和 App 端都可以使用，只需提供不同的适配器
 * 
 * 💡 设计理念：
 * - "给我一个能发送请求的工具，我就能为你生成所有 API 方法"
 * - 请求客户端是"工具"，API 方法是"产品"，工厂函数是"生产线"
 * 
 * 📖 使用方式：
 * 
 * **Web 端（Vben Admin + axios）**：
 * ```typescript
 * // 1. 创建适配器（让 Vben 的 requestClient 符合 IRequestClient 接口）
 * import { requestClient } from '#/api/request'
 * import type { IRequestClient } from '@bayes-mes/shared/api/core/client'
 * 
 * const webAdapter: IRequestClient = {
 *   get: (url, config) => requestClient.get(url, config).then(res => res.data),
 *   post: (url, data, config) => requestClient.post(url, data, config).then(res => res.data),
 *   // ... 其他方法
 * }
 * 
 * // 2. 调用工厂函数，传入适配器
 * import { createBomApi } from '@bayes-mes/shared/api/bom/factory'
 * export const bomApi = createBomApi(webAdapter)
 * 
 * // 3. 使用生成的 API
 * const result = await bomApi.getBomList({ page: 1 })
 * ```
 * 
 * **App 端（uni-app X）**：
 * ```typescript
 * // 1. 创建适配器（让 uni.request 符合 IRequestClient 接口）
 * const uniAdapter: IRequestClient = {
 *   get: (url, config) => new Promise((resolve, reject) => {
 *     uni.request({
 *       url,
 *       method: 'GET',
 *       data: config?.params,
 *       success: (res) => resolve(res.data.data),
 *       fail: reject
 *     })
 *   }),
 *   // ... 其他方法
 * }
 * 
 * // 2. 调用工厂函数
 * import { createBomApi } from '@bayes-mes/shared/api/bom/factory'
 * export const bomApi = createBomApi(uniAdapter)
 * ```
 * 
 * ⚠️ 注意事项：
 * - **请求客户端接口**：所有工厂函数都使用 `api/core/client.ts` 中定义的 `IRequestClient` 接口
 * - **不要重复定义接口**：从 `../core/client` 导入，不要在每个模块中重新定义
 * - **适配器职责**：适配器负责将平台特定的请求客户端转换为符合 `IRequestClient` 的对象
 * - **响应数据解包**：适配器需要处理响应数据的解包（如 `res.data.data`）
 * - **类型安全**：所有方法都有完整的 TypeScript 类型定义
 * - **注释详细**：每个方法都有详细的中文注释，包括使用场景、参数、返回值、数据流向
 * 
 * 🔄 更新时机：
 * - 后端新增 API 方法
 * - 后端修改参数或响应结构
 * - 后端修改路由路径
 * 
 * ✅ 验证方式：
 * - 确保所有 Controller 的 public 方法都已映射
 * - 确保所有类型导入正确
 * - 确保 URL 路径与后端路由一致
 * - 运行 `npm run type-check` 验证 TypeScript 编译
 * 
 * @module api/bom/factory
 * @description BOM管理模块 API 工厂函数
 * @generated AI-assisted
 * @author AI Assistant
 * @created 2025-10-18
 * @last-updated 2025-10-18
 * 
 * @change-log
 * - 2025-10-18: 初始创建，实现所有基础 CRUD、明细管理、展开分析、生命周期管理方法
 * ============================================================================
 */

// ===== 导入类型定义 =====
import type {
  Bom,                          // BOM主实体类型
  BomItem,                      // BOM明细类型
  BomQueryParams,               // BOM列表查询参数
  BomExpansionQueryParams,      // BOM展开参数
  BomCostCalculationQueryParams,// 成本计算参数
  BomStockCheckQueryParams,     // 库存检查参数
  CreateBomForm,                // 创建BOM表单
  UpdateBomForm,                // 更新BOM表单
  CreateBomItemForm,            // 创建明细表单
  UpdateBomItemForm,            // 更新明细表单
  CopyBomForm,                  // 复制BOM表单
  BomGlobalStatistics,          // 全局统计数据
  BomStatistics,                // 单个BOM统计
  BomExpansionResult,           // BOM展开结果
  BomTreeNode,                  // BOM树形节点
  BomCostCalculation,           // 成本计算结果
  WhereUsedResult,              // Where Used查询结果
  StockAvailabilityCheck,       // 库存可用性检查
} from '../../types/bom'

// ===== 导入通用类型 =====
import type { PaginationResult } from '../../types/api'

// ===== 导入统一的请求客户端接口 =====
// ⚠️ 重要：所有工厂函数都从这里导入接口，不要重复定义！
import type { IRequestClient } from '../core/client'

// ===== 导入 API 路由定义 =====
import { API_ROUTES } from '../routes'

// ===== 导出工厂函数 =====
/**
 * 创建 BOM管理模块的 API 方法集合
 * 
 * 这是一个工厂函数，它接收一个"请求客户端"作为参数，
 * 返回一个包含所有 BOM 相关 API 方法的对象。
 * 
 * @param client - 请求客户端，必须实现 IRequestClient 接口
 *   - 可以是 Web 端的 axios 适配器
 *   - 可以是 App 端的 uni.request 适配器
 *   - 可以是测试用的 Mock 客户端
 * 
 * @returns API 方法集合对象，包含所有 BOM 相关的请求方法
 * 
 * 为什么这样设计？
 * - 解耦：API 逻辑与具体的请求实现分离
 * - 复用：同一套 API 定义可以在 Web 和 App 端使用
 * - 测试：可以注入 Mock 客户端进行单元测试
 */
export function createBomApi(client: IRequestClient) {
  return {
    // ===== 基础 CRUD 操作 =====
    
    /**
     * 获取BOM列表（分页）
     * 
     * 使用场景：
     * - 在BOM管理页面的主列表中使用
     * - 支持分页、搜索、筛选功能（按产品物料、状态、生命周期状态等）
     * - 用户可以查看所有BOM或按条件筛选
     * 
     * @param params - 查询参数对象（可选）
     *   - page?: number - 页码，从 1 开始，默认 1
     *     示例：page: 1（第一页），page: 2（第二页）
     *   - per_page?: number - 每页数量，默认 20
     *     示例：per_page: 20（每页20条）
     *   - keyword?: string - 搜索关键词，搜索BOM编码或BOM名称
     *     示例：keyword: '手机'（搜索包含"手机"的BOM）
     *   - material_id?: number - 按产品物料ID筛选
     *     示例：material_id: 1001（查询产品物料ID为1001的BOM）
     *   - status?: BomStatus - 按状态筛选（1-启用 0-停用）
     *     示例：status: 1（只显示启用的BOM）
     *   - lifecycle_status?: BomLifecycleStatus - 按生命周期状态筛选
     *     示例：lifecycle_status: 'RELEASED'（只显示已发布的BOM）
     *   - factory_id?: number - 按工厂ID筛选
     *     示例：factory_id: 1（查询工厂ID为1的BOM）
     *   - sort_field?: string - 排序字段
     *     示例：sort_field: 'created_at'（按创建时间排序）
     *   - sort_order?: 'asc' | 'desc' - 排序方向
     *     示例：sort_order: 'desc'（降序）
     * 
     * @returns Promise<PaginationResult<Bom>> 分页结果对象
     *   返回的数据结构：
     *   {
     *     data: Bom[],             // BOM列表数组
     *     total: number,           // 总记录数，如 158
     *     current_page: number,    // 当前页码，如 2
     *     per_page: number,        // 每页数量，如 20
     *     last_page: number,       // 最后一页页码，如 8
     *     from: number,            // 当前页起始记录号，如 21
     *     to: number               // 当前页结束记录号，如 40
     *   }
     * 
     * 数据流向（完整过程）：
     * 1. 用户操作：用户在BOM管理页面点击"下一页"或输入搜索关键词
     * 2. 前端调用：组件调用 bomApi.getBomList({ page: 2, keyword: '手机' })
     * 3. 适配器处理：适配器将参数转换为 HTTP 请求
     *    - Web端：axios 发送 GET 请求
     *    - App端：uni.request 发送 GET 请求
     * 4. 网络传输：发送到后端 GET /api/v1/boms?page=2&keyword=手机
     * 5. 后端处理：
     *    - 路由匹配到 BomController@index 方法
     *    - 验证查询参数（BomQueryParams）
     *    - 调用 BomService::list() 查询数据库
     *    - 应用筛选条件（关键字、状态等）和分页
     *    - 通过 BomResource 格式化数据（snake_case → camelCase）
     * 6. 响应返回：后端返回 JSON 格式的分页数据
     * 7. 适配器解包：适配器提取 response.data.data（实际数据）
     * 8. 前端接收：组件收到 PaginationResult<Bom> 类型的数据
     * 9. 页面更新：使用数据更新表格显示
     * 
     * 代码示例：
     * ```typescript
     * // 示例1：获取第一页（使用默认参数）
     * const page1 = await bomApi.getBomList()
     * console.log(page1.data)  // BOM对象数组
     * console.log(page1.total) // 总记录数
     * 
     * // 示例2：获取第二页，每页20条
     * const page2 = await bomApi.getBomList({
     *   page: 2,
     *   per_page: 20
     * })
     * 
     * // 示例3：搜索功能
     * const searchResult = await bomApi.getBomList({
     *   keyword: '手机'
     * })
     * 
     * // 示例4：按状态筛选
     * const releasedBoms = await bomApi.getBomList({
     *   lifecycle_status: 'RELEASED'  // 只显示已发布的BOM
     * })
     * 
     * // 示例5：组合查询（分页 + 搜索 + 排序）
     * const result = await bomApi.getBomList({
     *   page: 1,
     *   per_page: 10,
     *   keyword: '手机',
     *   status: 1,
     *   sort_field: 'created_at',
     *   sort_order: 'desc'
     * })
     * ```
     * 
     * 错误处理：
     * - 404：资源不存在（路由错误或后端未实现）
     * - 422：参数验证失败（page 不是数字等）
     * - 500：服务器内部错误（数据库查询失败等）
     */
    getBomList(params?: BomQueryParams) {
      return client.get<PaginationResult<Bom>>(API_ROUTES.bom.list, { params })
    },
    
    /**
     * 获取BOM详情
     * 
     * 使用场景：
     * - 在BOM详情页面显示完整信息（包含明细列表）
     * - 编辑BOM时获取当前数据
     * - 查看BOM的版本、生命周期状态、明细等详细信息
     * 
     * @param id - BOM的唯一标识符（主键）
     *   示例：id: 123（获取 ID 为 123 的BOM）
     * 
     * @returns Promise<Bom> 单个BOM对象（包含关联的物料、明细等）
     *   返回的数据结构：
     *   {
     *     id: number,              // BOM ID
     *     bomCode: string,         // BOM编码
     *     bomName: string,         // BOM名称
     *     materialId: number,      // 产品物料ID
     *     version: string,         // 版本号
     *     quantity: number,        // 基准数量
     *     unit: string,            // 单位
     *     isDefault: boolean,      // 是否默认版本
     *     status: number,          // 状态
     *     lifecycleStatus: string, // 生命周期状态
     *     material: {...},         // 关联的产品物料信息
     *     items: BomItem[],        // BOM明细列表
     *     createdAt: string,       // 创建时间（ISO 8601格式）
     *     updatedAt: string        // 更新时间
     *   }
     * 
     * 数据流向：
     * 1. 用户点击BOM列表中的某一行，或在 URL 中输入详情页地址
     * 2. 前端调用：bomApi.getBomDetail(123)
     * 3. 发送请求：GET /api/v1/boms/123
     * 4. 后端处理：BomController@show 方法
     *    - 验证 ID 参数
     *    - 调用 BomService::getById() 查询数据
     *    - 预加载关联数据（material, items.material）
     *    - 如果不存在，返回 404
     *    - 通过 BomResource 格式化数据
     * 5. 返回数据：单个 Bom 对象（包含完整的关联信息）
     * 6. 前端显示：在详情页或编辑表单中展示数据
     * 
     * 代码示例：
     * ```typescript
     * // 获取 ID 为 123 的BOM
     * const detail = await bomApi.getBomDetail(123)
     * console.log(detail.bomName)  // BOM名称
     * console.log(detail.bomCode)  // BOM编码
     * console.log(detail.items)    // BOM明细列表
     * 
     * // 在详情页组件中使用
     * const { id } = useRoute().params
     * const bomData = await bomApi.getBomDetail(Number(id))
     * ```
     * 
     * 错误处理：
     * - 404：BOM不存在（可能已被删除）
     * - 403：无权限访问该BOM
     * - 422：ID 格式错误（不是数字）
     */
    getBomDetail(id: number) {
      return client.get<Bom>(API_ROUTES.bom.detail(id))
    },
    
    /**
     * 创建新BOM
     * 
     * 使用场景：
     * - 在BOM管理页面点击"新建"按钮
     * - 提交创建BOM的表单
     * - 为新产品创建物料清单
     * 
     * @param data - 创建BOM的表单数据
     *   字段要求（参考 CreateBomForm 类型）：
     *   - bom_code: string - BOM编码（必填，唯一）
     *     示例：'BOM-2025-001'
     *   - bom_name: string - BOM名称（必填）
     *     示例：'手机主板BOM'
     *   - material_id: number - 产品物料ID（必填）
     *     示例：1001（对应物料表的ID）
     *   - version: string - 版本号（必填）
     *     示例：'1.0', '2.0', '2.1'
     *   - quantity: number - 基准数量（必填）
     *     示例：1.0（表示1个产品的用量）
     *   - unit: string - 单位（必填）
     *     示例：'PCS', 'SET', 'KG'
     *   - is_default?: boolean - 是否默认版本（可选）
     *     示例：true（设为该产品的默认版本）
     *   - status?: number - 状态（可选，默认1）
     *     示例：1（启用）
     *   - lifecycle_status?: string - 生命周期状态（可选，默认DESIGN）
     *     示例：'DESIGN'（设计中）
     *   - effective_date?: string - 生效日期（可选）
     *   - expiry_date?: string - 失效日期（可选）
     *   - description?: string - 描述（可选）
     *   - remark?: string - 备注（可选）
     *   - factory_code?: string - 工厂编码（可选）
     *   - factory_id?: number - 工厂ID（可选）
     * 
     * @returns Promise<Bom> 创建成功后的BOM对象（包含生成的 ID）
     *   返回的数据：
     *   {
     *     id: number,              // 自动生成的 ID
     *     bomCode: string,         // 提交的编码
     *     bomName: string,         // 提交的名称
     *     materialId: number,      // 产品物料ID
     *     version: string,         // 版本号
     *     quantity: number,        // 基准数量
     *     unit: string,            // 单位
     *     isDefault: boolean,      // 是否默认版本
     *     lifecycleStatus: string, // 生命周期状态
     *     createdAt: string,       // 服务器生成的创建时间
     *     updatedAt: string,       // 服务器生成的更新时间
     *     // ... 其他字段
     *   }
     * 
     * 数据流向：
     * 1. 用户填写创建BOM表单，点击"提交"按钮
     * 2. 前端验证：表单验证（必填项、格式等）
     * 3. 前端调用：bomApi.createBom({ bom_code: 'BOM-001', bom_name: '手机主板', ... })
     * 4. 发送请求：POST /api/v1/boms
     *    请求体：{ bom_code: 'BOM-001', bom_name: '手机主板', ... }（JSON格式）
     * 5. 后端处理：BomController@store 方法
     *    - 验证数据（使用 CreateBomRequest）
     *    - 检查唯一性（BOM编码不能重复）
     *    - 检查产品物料是否存在
     *    - 检查循环依赖（如果有父级BOM）
     *    - 调用 BomService::create() 插入数据库
     *    - 如果设为默认版本，取消同产品的其他默认版本
     *    - 记录审计日志
     *    - 返回新创建的记录（包含自动生成的 ID 和时间戳）
     * 6. 前端处理：
     *    - 显示成功提示
     *    - 跳转到BOM详情页
     *    - 刷新BOM列表数据
     * 
     * 代码示例：
     * ```typescript
     * // 创建新BOM
     * const newData: CreateBomForm = {
     *   bom_code: 'BOM-2025-001',
     *   bom_name: '手机主板BOM',
     *   material_id: 1001,
     *   version: '1.0',
     *   quantity: 1.0,
     *   unit: 'PCS',
     *   is_default: true,
     *   lifecycle_status: 'DESIGN',
     *   description: '手机主板物料清单'
     * }
     * 
     * const created = await bomApi.createBom(newData)
     * console.log(created.id)  // 新创建的 ID
     * 
     * // 在表单提交中使用
     * const handleSubmit = async (formData: CreateBomForm) => {
     *   try {
     *     const result = await bomApi.createBom(formData)
     *     message.success('BOM创建成功')
     *     router.push(`/bom/${result.id}`)  // 跳转到详情页
     *   } catch (error) {
     *     message.error('创建失败：' + error.message)
     *   }
     * }
     * ```
     * 
     * 错误处理：
     * - 422：数据验证失败
     *   - 必填字段缺失
     *   - 字段格式错误
     *   - BOM编码重复（违反唯一性约束）
     *   - 产品物料不存在
     *   - 检测到循环依赖
     * - 403：无权限创建BOM
     * - 500：服务器错误（数据库插入失败）
     */
    createBom(data: CreateBomForm) {
      return client.post<Bom>(API_ROUTES.bom.create, data)
    },
    
    /**
     * 更新BOM
     * 
     * 使用场景：
     * - 在BOM编辑页面修改后提交
     * - 更新BOM的版本、数量、状态等信息
     * - 只能在设计中或待审核状态下编辑
     * 
     * @param id - 要更新的BOM的 ID
     * @param data - 更新的数据（只需要包含要修改的字段）
     *   字段要求（参考 UpdateBomForm 类型）：
     *   - 可以只包含部分字段（部分更新）
     *   - 不能修改的字段：id, material_id, created_by, createdAt（后端会忽略）
     *   - bom_code?: string - BOM编码（可选）
     *   - bom_name?: string - BOM名称（可选）
     *   - version?: string - 版本号（可选）
     *   - quantity?: number - 基准数量（可选）
     *   - unit?: string - 单位（可选）
     *   - is_default?: boolean - 是否默认版本（可选）
     *   - status?: number - 状态（可选）
     *   - effective_date?: string - 生效日期（可选）
     *   - expiry_date?: string - 失效日期（可选）
     *   - lifecycle_status?: string - 生命周期状态（可选）
     *   - description?: string - 描述（可选）
     *   - remark?: string - 备注（可选）
     * 
     * @returns Promise<Bom> 更新后的完整BOM对象
     * 
     * 数据流向：
     * 1. 用户在BOM编辑页面修改字段，点击"保存"
     * 2. 前端调用：bomApi.updateBom(123, { bom_name: '新名称' })
     * 3. 发送请求：PUT /api/v1/boms/123
     *    请求体：{ bom_name: '新名称' }（只包含修改的字段）
     * 4. 后端处理：BomController@update 方法
     *    - 验证 ID 是否存在
     *    - 检查是否可编辑（只有设计中/待审核状态可编辑）
     *    - 验证更新数据（使用 UpdateBomRequest）
     *    - 检查唯一性（如果修改了编码）
     *    - 调用 BomService::update() 更新数据库记录
     *    - 如果设为默认版本，取消同产品的其他默认版本
     *    - 记录审计日志
     *    - 返回更新后的完整数据
     * 5. 前端处理：
     *    - 显示成功提示
     *    - 更新页面显示的数据
     * 
     * 代码示例：
     * ```typescript
     * // 更新BOM的名称
     * const updated = await bomApi.updateBom(123, {
     *   bom_name: '新名称'
     * })
     * 
     * // 更新多个字段
     * const updated2 = await bomApi.updateBom(123, {
     *   bom_name: '新名称',
     *   quantity: 2.0,
     *   description: '更新描述'
     * })
     * 
     * // 在编辑表单中使用
     * const handleUpdate = async (id: number, formData: UpdateBomForm) => {
     *   try {
     *     const result = await bomApi.updateBom(id, formData)
     *     message.success('更新成功')
     *     bomData.value = result  // 更新本地数据
     *   } catch (error) {
     *     if (error.message.includes('不允许编辑')) {
     *       message.error('BOM已发布，不能编辑')
     *     } else {
     *       message.error('更新失败：' + error.message)
     *     }
     *   }
     * }
     * ```
     * 
     * 错误处理：
     * - 404：BOM不存在
     * - 422：数据验证失败
     *   - BOM编码重复
     *   - 字段格式错误
     * - 400：BOM状态不允许编辑（已发布、冻结、淘汰）
     * - 403：无权限修改
     */
    updateBom(id: number, data: UpdateBomForm) {
      return client.put<Bom>(API_ROUTES.bom.boms(id), data)
    },
    
    /**
     * 删除BOM
     * 
     * 使用场景：
     * - 在BOM列表中点击"删除"按钮
     * - 在BOM详情页删除当前记录
     * - 删除不需要的BOM版本
     * 
     * 删除规则：
     * - 设计中/待审核状态：可以直接删除
     * - 已发布状态：需要检查是否有在产工单，无在产工单才可删除
     * - 冻结/淘汰状态：可以删除（历史数据）
     * - 有子BOM：需要先删除子BOM
     * 
     * @param id - 要删除的BOM的 ID
     * 
     * @returns Promise<void> 删除成功不返回数据
     * 
     * 数据流向：
     * 1. 用户点击删除按钮，弹出确认对话框
     * 2. 用户确认删除
     * 3. 前端调用：bomApi.deleteBom(123)
     * 4. 发送请求：DELETE /api/v1/boms/123
     * 5. 后端处理：BomController@destroy 方法
     *    - 验证 ID 是否存在
     *    - 调用 BomService::delete() 检查是否可以删除
     *    - 检查生命周期状态和在产工单
     *    - 检查是否有子BOM
     *    - 软删除记录（deleted_at）
     *    - 记录审计日志
     *    - 返回成功响应
     * 6. 前端处理：
     *    - 显示成功提示
     *    - 从列表中移除该记录
     *    - 刷新列表数据
     * 
     * 代码示例：
     * ```typescript
     * // 删除指定BOM
     * await bomApi.deleteBom(123)
     * console.log('删除成功')
     * 
     * // 在列表中使用（带确认）
     * const handleDelete = async (id: number) => {
     *   const confirmed = await showConfirm('确定要删除该BOM吗？')
     *   if (!confirmed) return
     *   
     *   try {
     *     await bomApi.deleteBom(id)
     *     message.success('删除成功')
     *     await loadList()  // 刷新列表
     *   } catch (error) {
     *     if (error.message.includes('不允许删除')) {
     *       message.error('BOM有在产工单，不能删除')
     *     } else if (error.message.includes('存在子级')) {
     *       message.error('请先删除子BOM')
     *     } else {
     *       message.error('删除失败：' + error.message)
     *     }
     *   }
     * }
     * ```
     * 
     * 错误处理：
     * - 404：BOM不存在（可能已被删除）
     * - 403：无权限删除
     * - 400：无法删除
     *   - BOM有在产工单
     *   - BOM存在子级
     *   - BOM状态不允许删除
     */
    deleteBom(id: number) {
      return client.delete<void>(API_ROUTES.bom.delete(id))
    },

    /**
     * 批量删除BOM
     * 
     * 使用场景：
     * - BOM列表中勾选多个记录批量删除
     * - 清理测试数据
     * - 批量维护BOM数据
     * 
     * 删除规则：
     * - 每个BOM独立验证，部分失败不影响其他BOM
     * - 遵循单个删除的所有规则（状态检查、工单检查、子级检查）
     * - 返回详细的删除结果（成功数、失败数、错误信息）
     * 
     * @param ids - BOM ID数组
     * 
     * @returns Promise 删除结果
     *   - deleted: 成功删除的数量
     *   - failed: 删除失败的数量
     *   - errors: 错误信息列表
     *   - total: 总数量
     * 
     * 数据流向：
     * 1. 用户在列表中勾选多个BOM
     * 2. 点击"批量删除"按钮
     * 3. 弹出确认对话框
     * 4. 前端调用：bomApi.batchDeleteBoms([1, 2, 3])
     * 5. 发送请求：POST /api/v1/boms/batch-delete
     * 6. 后端处理：BomController@batchDestroy 方法
     *    - 验证参数格式
     *    - 遍历ID列表，逐个调用 delete()
     *    - 每个BOM独立检查和删除
     *    - 记录成功和失败情况
     *    - 返回删除结果统计
     * 7. 前端处理：
     *    - 显示删除结果提示
     *    - 刷新列表数据
     *    - 处理部分失败情况
     * 
     * 代码示例：
     * ```typescript
     * // 批量删除BOM
     * const selectedIds = [1, 2, 3, 4, 5]
     * const result = await bomApi.batchDeleteBoms(selectedIds)
     * 
     * console.log(`成功删除 ${result.deleted} 个BOM`)
     * console.log(`删除失败 ${result.failed} 个BOM`)
     * 
     * // 显示详细错误信息
     * if (result.errors.length > 0) {
     *   result.errors.forEach(error => {
     *     console.error(error)
     *   })
     * }
     * 
     * // 在列表中使用（带确认）
     * const handleBatchDelete = async (ids: number[]) => {
     *   const confirmed = await showConfirm(`确定要删除 ${ids.length} 个BOM吗？`)
     *   if (!confirmed) return
     *   
     *   try {
     *     const result = await bomApi.batchDeleteBoms(ids)
     *     
     *     if (result.failed === 0) {
     *       message.success(`成功删除 ${result.deleted} 个BOM`)
     *     } else {
     *       message.warning(
     *         `成功删除 ${result.deleted} 个BOM，失败 ${result.failed} 个`
     *       )
     *       // 显示详细错误信息
     *       result.errors.forEach(error => {
     *         message.error(error, { duration: 5000 })
     *       })
     *     }
     *     
     *     await loadList()  // 刷新列表
     *   } catch (error) {
     *     message.error('批量删除失败：' + error.message)
     *   }
     * }
     * ```
     * 
     * 错误处理：
     * - 422：参数验证失败（ids为空或格式错误）
     * - 403：无权限删除
     * - 部分失败：返回结果中的 errors 数组包含详细错误信息
     */
    batchDeleteBoms(ids: number[]) {
      return client.post<{
        deleted: number
        failed: number
        errors: string[]
        total: number
      }>(API_ROUTES.bom.batchDelete, { ids })
    },
    
    // ===== BOM明细管理 =====
    
    /**
     * 添加BOM明细
     * 
     * 使用场景：
     * - 在BOM详情页点击"添加明细"
     * - 为BOM添加组成物料
     * - 指定物料用量、损耗率、工序等信息
     * 
     * @param bomId - BOM ID
     * @param data - 明细数据
     *   - material_id: number - 子物料ID（必填）
     *   - line_number?: number - 行号（可选，默认自动递增）
     *   - quantity: number - 标准用量（必填）
     *   - unit: string - 单位（必填）
     *   - scrap_rate?: number - 损耗率（可选，0-100）
     *   - operation_code?: string - 使用工序编码（可选）
     *   - operation_seq?: number - 工序顺序（可选）
     *   - position_code?: string - 位置编码（可选）
     *   - position_description?: string - 位置描述（可选）
     *   - status?: number - 状态（可选，默认1）
     *   - remark?: string - 备注（可选）
     * 
     * @returns Promise<BomItem> 创建的明细信息
     * 
     * 代码示例：
     * ```typescript
     * // 添加明细
     * const newItem = await bomApi.addBomItem(123, {
     *   material_id: 2001,
     *   quantity: 2.5,
     *   unit: 'PCS',
     *   scrap_rate: 5.0,  // 5%损耗率
     *   operation_code: 'OP-010',
     *   remark: '主要原材料'
     * })
     * ```
     */
    addBomItem(bomId: number, data: CreateBomItemForm) {
      return client.post<BomItem>(API_ROUTES.bom.addItem(bomId), data)
    },
    
    /**
     * 更新BOM明细
     * 
     * 使用场景：
     * - 在BOM明细列表中修改用量
     * - 调整损耗率
     * - 修改工序信息
     * 
     * @param itemId - 明细ID
     * @param data - 更新数据
     * 
     * @returns Promise<BomItem> 更新后的明细信息
     * 
     * 代码示例：
     * ```typescript
     * // 更新用量和损耗率
     * const updated = await bomApi.updateBomItem(456, {
     *   quantity: 3.0,
     *   scrap_rate: 10.0
     * })
     * ```
     */
    updateBomItem(itemId: number, data: UpdateBomItemForm) {
      return client.put<BomItem>(API_ROUTES.bom.bomItems(itemId), data)
    },
    
    /**
     * 删除BOM明细
     * 
     * 使用场景：
     * - 在BOM明细列表中删除不需要的物料
     * - 移除错误添加的明细
     * 
     * @param itemId - 明细ID
     * 
     * 代码示例：
     * ```typescript
     * // 删除明细
     * await bomApi.deleteBomItem(456)
     * ```
     */
    deleteBomItem(itemId: number) {
      return client.delete<void>(API_ROUTES.bom.bomItems(itemId))
    },
    
    // ===== BOM展开与分析 =====
    
    /**
     * BOM展开（获取物料需求）
     * 
     * 使用场景：
     * - 生产计划：计算生产指定数量产品需要的所有原材料
     * - 采购计划：确定需要采购的物料清单
     * - 成本分析：分析产品的物料成本构成
     * 
     * @param id - BOM ID
     * @param params - 展开参数
     *   - quantity?: number - 产品数量（默认1.0）
     *     示例：quantity: 100（计算生产100个产品的物料需求）
     *   - aggregate?: boolean - 是否聚合相同物料（默认false）
     *     示例：aggregate: true（合并相同物料，显示总用量）
     * 
     * @returns Promise<BomExpansionResult[]> 展开结果（物料需求列表）
     *   返回数据结构：
     *   [
     *     {
     *       materialId: number,        // 物料ID
     *       materialCode: string,      // 物料编码
     *       materialName: string,      // 物料名称
     *       specification: string,     // 规格型号
     *       requiredQuantity: number,  // 需求数量（含损耗）
     *       unit: string,              // 单位
     *       level: number,             // 层级（0=产品，1=一级子件，2=二级子件...）
     *       parentMaterialId: number,  // 父级物料ID
     *       hasSubstitutes: boolean    // 是否有替代料
     *     },
     *     // ... 更多物料
     *   ]
     * 
     * 数据流向：
     * 1. 用户在生产计划页面输入产品数量（如100个）
     * 2. 前端调用：bomApi.expandBom(123, { quantity: 100, aggregate: true })
     * 3. 发送请求：GET /api/v1/boms/123/expand?quantity=100&aggregate=true
     * 4. 后端处理：BomController@expand 方法
     *    - 调用 BomExpansionService::expand() 或 expandAndAggregate()
     *    - 递归展开BOM结构
     *    - 计算每个物料的需求数量（考虑损耗率）
     *    - 如果 aggregate=true，合并相同物料
     *    - 通过 BomExpansionResult 格式化数据
     * 5. 返回数据：物料需求列表
     * 6. 前端显示：在物料需求表格中展示
     * 
     * 代码示例：
     * ```typescript
     * // 示例1：展开BOM，显示层级结构
     * const materials = await bomApi.expandBom(123, {
     *   quantity: 100  // 生产100个产品
     * })
     * console.log(materials)  // 显示完整的物料树
     * 
     * // 示例2：聚合展开，合并相同物料
     * const aggregated = await bomApi.expandBom(123, {
     *   quantity: 100,
     *   aggregate: true  // 合并相同物料
     * })
     * console.log(aggregated)  // 每个物料只显示一次，数量为总需求
     * 
     * // 示例3：用于生产计划
     * const planQuantity = 500  // 计划生产500个
     * const requirements = await bomApi.expandBom(bomId, {
     *   quantity: planQuantity,
     *   aggregate: true
     * })
     * // 显示采购清单
     * requirements.forEach(item => {
     *   console.log(`${item.materialCode} ${item.materialName}: ${item.requiredQuantity} ${item.unit}`)
     * })
     * ```
     */
    expandBom(id: number, params?: BomExpansionQueryParams) {
      return client.get<BomExpansionResult[]>(API_ROUTES.bom.expand(id), { params })
    },
    
    /**
     * 获取BOM树形结构
     * 
     * 使用场景：
     * - 在BOM详情页显示树形结构图
     * - 可视化展示BOM的层级关系
     * - 查看产品的组成结构
     * 
     * @param id - BOM ID
     * 
     * @returns Promise<BomTreeNode> BOM树形节点（递归包含子节点）
     *   返回数据结构：
     *   {
     *     id: number,               // BOM ID
     *     bomCode: string,          // BOM编码
     *     bomName: string,          // BOM名称
     *     materialId: number,       // 物料ID
     *     materialCode: string,     // 物料编码
     *     materialName: string,     // 物料名称
     *     quantity: number,         // 数量
     *     unit: string,             // 单位
     *     level: number,            // 层级
     *     children?: BomTreeNode[]  // 子节点（递归结构）
     *   }
     * 
     * 代码示例：
     * ```typescript
     * // 获取树形结构
     * const tree = await bomApi.getBomTree(123)
     * console.log(tree.children)  // 一级子件
     * console.log(tree.children[0].children)  // 二级子件
     * 
     * // 用于树形组件
     * <Tree data={tree} />
     * ```
     */
    getBomTree(id: number) {
      return client.get<BomTreeNode>(API_ROUTES.bom.bomsTree(id))
    },
    
    /**
     * 计算BOM成本
     * 
     * 使用场景：
     * - 产品定价：计算产品的物料成本
     * - 成本分析：分析成本构成
     * - 报价单：为客户提供报价
     * 
     * @param id - BOM ID
     * @param params - 计算参数
     *   - quantity?: number - 产品数量（默认1.0）
     * 
     * @returns Promise<BomCostCalculation> 成本计算结果
     *   返回数据结构：
     *   {
     *     bomId: number,            // BOM ID
     *     productQuantity: number,  // 产品数量
     *     materialCost: number,     // 物料成本
     *     manufacturingCost: number,// 制造成本（可选）
     *     totalCost: number,        // 总成本
     *     itemCosts: [              // 明细成本列表
     *       {
     *         materialId: number,
     *         materialCode: string,
     *         materialName: string,
     *         quantity: number,
     *         unitCost: number,     // 单价
     *         totalCost: number     // 小计
     *       },
     *       // ... 更多明细
     *     ]
     *   }
     * 
     * 代码示例：
     * ```typescript
     * // 计算100个产品的成本
     * const cost = await bomApi.calculateBomCost(123, { quantity: 100 })
     * console.log(`总成本：${cost.totalCost}元`)
     * console.log(`单位成本：${cost.totalCost / cost.productQuantity}元/个`)
     * 
     * // 显示成本明细
     * cost.itemCosts.forEach(item => {
     *   console.log(`${item.materialName}: ${item.totalCost}元`)
     * })
     * ```
     */
    calculateBomCost(id: number, params?: BomCostCalculationQueryParams) {
      return client.get<BomCostCalculation>(API_ROUTES.bom.cost(id), { params })
    },
    
    /**
     * 检查库存可用性
     * 
     * 使用场景：
     * - 生产前检查：确认是否有足够库存
     * - 采购计划：确定缺料情况
     * - 预警提示：库存不足时提醒
     * 
     * @param id - BOM ID
     * @param params - 检查参数
     *   - quantity?: number - 计划生产数量（默认1.0）
     * 
     * @returns Promise<StockAvailabilityCheck> 库存检查结果
     *   返回数据结构：
     *   {
     *     bomId: number,           // BOM ID
     *     productQuantity: number, // 产品数量
     *     isAvailable: boolean,    // 是否可用（库存是否充足）
     *     shortages: [             // 缺料列表
     *       {
     *         materialId: number,
     *         materialCode: string,
     *         materialName: string,
     *         requiredQuantity: number,   // 需求数量
     *         availableQuantity: number,  // 可用库存
     *         shortageQuantity: number,   // 缺料数量
     *         unit: string
     *       },
     *       // ... 更多缺料
     *     ]
     *   }
     * 
     * 代码示例：
     * ```typescript
     * // 检查能否生产100个产品
     * const check = await bomApi.checkBomStock(123, { quantity: 100 })
     * 
     * if (check.isAvailable) {
     *   console.log('库存充足，可以生产')
     * } else {
     *   console.log('库存不足，缺料清单：')
     *   check.shortages.forEach(item => {
     *     console.log(`${item.materialName}: 需要${item.requiredQuantity}，现有${item.availableQuantity}，缺${item.shortageQuantity}${item.unit}`)
     *   })
     * }
     * ```
     */
    checkBomStock(id: number, params?: BomStockCheckQueryParams) {
      return client.get<StockAvailabilityCheck>(API_ROUTES.bom.checkStock(id), { params })
    },
    
    /**
     * Where Used 查询（反向查询物料使用情况）
     * 
     * 使用场景：
     * - 物料停产：查询哪些BOM用到了该物料
     * - 物料替换：确定影响范围
     * - 变更管理：评估变更影响
     * 
     * @param materialId - 物料ID
     * 
     * @returns Promise<WhereUsedResult> 使用情况
     *   返回数据结构：
     *   {
     *     materialId: number,       // 物料ID
     *     materialCode: string,     // 物料编码
     *     materialName: string,     // 物料名称
     *     usedInBoms: [             // 使用该物料的BOM列表
     *       {
     *         bomId: number,
     *         bomCode: string,
     *         bomName: string,
     *         materialCode: string,  // 产品物料编码
     *         materialName: string,  // 产品物料名称
     *         quantity: number,      // 用量
     *         unit: string
     *       },
     *       // ... 更多BOM
     *     ]
     *   }
     * 
     * 代码示例：
     * ```typescript
     * // 查询物料2001在哪些BOM中使用
     * const whereUsed = await bomApi.getWhereUsed(2001)
     * 
     * console.log(`物料 ${whereUsed.materialCode} ${whereUsed.materialName}`)
     * console.log(`使用该物料的BOM：`)
     * whereUsed.usedInBoms.forEach(bom => {
     *   console.log(`- ${bom.bomCode} ${bom.bomName}（产品：${bom.materialName}），用量：${bom.quantity}${bom.unit}`)
     * })
     * ```
     */
    getWhereUsed(materialId: number) {
      return client.get<WhereUsedResult>(API_ROUTES.material.whereUsed(materialId))
    },
    
    // ===== BOM生命周期管理 =====
    
    /**
     * 审核BOM
     * 
     * 使用场景：
     * - BOM设计完成后，提交审核
     * - 审核通过后，BOM状态从"设计中"变为"已发布"
     * - 已发布的BOM可用于生产
     * 
     * @param id - BOM ID
     * 
     * @returns Promise<Bom> 审核后的BOM（lifecycleStatus='RELEASED'）
     * 
     * 代码示例：
     * ```typescript
     * // 审核BOM
     * const approved = await bomApi.approveBom(123)
     * console.log(approved.lifecycleStatus)  // 'RELEASED'
     * ```
     */
    approveBom(id: number) {
      return client.post<Bom>(API_ROUTES.bom.approveApplication(id))
    },
    
    /**
     * 冻结BOM
     * 
     * 使用场景：
     * - 临时停用BOM
     * - 暂时不使用，但保留历史版本
     * 
     * @param id - BOM ID
     * 
     * @returns Promise<Bom> 冻结后的BOM（lifecycleStatus='FROZEN'）
     */
    freezeBom(id: number) {
      return client.post<Bom>(API_ROUTES.bom.freeze(id))
    },
    
    /**
     * 淘汰BOM
     * 
     * 使用场景：
     * - BOM已过时，不再使用
     * - 产品停产，BOM失效
     * 
     * @param id - BOM ID
     * 
     * @returns Promise<Bom> 淘汰后的BOM（lifecycleStatus='OBSOLETE', status=0）
     */
    obsoleteBom(id: number) {
      return client.post<Bom>(API_ROUTES.bom.obsolete(id))
    },
    
    // ===== BOM复制 =====
    
    /**
     * 复制BOM
     * 
     * 使用场景：
     * - 创建新版本BOM
     * - 为相似产品创建BOM（修改部分明细）
     * - 快速创建BOM模板
     * 
     * @param id - 源BOM ID
     * @param data - 复制参数
     *   - bom_code: string - 新BOM编码（必填，唯一）
     *   - bom_name: string - 新BOM名称（必填）
     *   - version: string - 新版本号（必填）
     *   - set_as_default?: boolean - 是否设为默认版本（可选）
     * 
     * @returns Promise<Bom> 新创建的BOM（包含复制的明细）
     * 
     * 代码示例：
     * ```typescript
     * // 复制BOM创建新版本
     * const newBom = await bomApi.copyBom(123, {
     *   bom_code: 'BOM-2025-002',
     *   bom_name: '手机主板BOM V2.0',
     *   version: '2.0',
     *   set_as_default: true
     * })
     * console.log(newBom.id)  // 新BOM的ID
     * console.log(newBom.items)  // 包含复制的明细
     * ```
     */
    copyBom(id: number, data: CopyBomForm) {
      return client.post<Bom>(API_ROUTES.bom.copy(id), data)
    },
    
    // ===== 统计信息 =====
    
    /**
     * 获取BOM全局统计信息
     * 
     * 使用场景：
     * - 在BOM管理首页显示统计卡片
     * - 数据看板展示
     * 
     * @returns Promise<BomGlobalStatistics> 全局统计信息
     *   返回数据结构：
     *   {
     *     totalBoms: number,           // BOM总数
     *     activeCount: number,         // 启用BOM数量
     *     inactiveCount: number,       // 停用BOM数量
     *     byLifecycleStatus: {         // 按生命周期状态分组
     *       DESIGN: number,
     *       PENDING: number,
     *       RELEASED: number,
     *       FROZEN: number,
     *       OBSOLETE: number
     *     },
     *     avgItemCount: number,        // 平均明细数量
     *     withItems: number,           // 有明细的BOM数量
     *     withoutItems: number,        // 无明细的BOM数量
     *     complexBoms: number          // 复杂BOM数量（明细>20）
     *   }
     * 
     * 代码示例：
     * ```typescript
     * // 获取统计信息
     * const stats = await bomApi.getBomGlobalStatistics()
     * console.log(`BOM总数：${stats.totalBoms}`)
     * console.log(`已发布：${stats.byLifecycleStatus.RELEASED}`)
     * console.log(`平均明细数：${stats.avgItemCount}`)
     * ```
     */
    getBomGlobalStatistics() {
      return client.get<BomGlobalStatistics>(API_ROUTES.bom.statistics)
    },
    
    /**
     * 获取单个BOM统计信息
     * 
     * 使用场景：
     * - 在BOM详情页显示统计卡片
     * - 显示BOM的明细数量、成本、使用情况等
     * 
     * @param id - BOM ID
     * 
     * @returns Promise<BomStatistics> BOM统计信息
     *   返回数据结构：
     *   {
     *     bomCode: string,           // BOM编码
     *     bomName: string,           // BOM名称
     *     materialCode: string,      // 物料编码
     *     materialName: string,      // 物料名称
     *     version: string,           // 版本号
     *     lifecycleStatus: string,   // 生命周期状态
     *     status: number,            // 状态
     *     itemCount: number,         // 明细数量
     *     estimatedTotalCost: number,// 预计总成本
     *     isComplex: boolean,        // 是否为复杂BOM（明细>20）
     *     hasSubstitutes: boolean,   // 是否有替代料
     *     usageInProduction: number  // 在生产中的使用次数
     *   }
     * 
     * 代码示例：
     * ```typescript
     * // 获取BOM统计
     * const stats = await bomApi.getBomStatistics(123)
     * console.log(`明细数量：${stats.itemCount}`)
     * console.log(`预计成本：${stats.estimatedTotalCost}元`)
     * ```
     */
    getBomStatistics(id: number) {
      return client.get<BomStatistics>(API_ROUTES.bom.bomsStatistics(id))
    },

    // ===== 扩展功能（v3.14.0 新增）=====

    /**
     * 设置BOM为默认版本
     * 
     * 使用场景：
     * - 在BOM列表中点击"设为默认"
     * - 将某个版本设置为该产品的默认BOM
     * - 自动取消同产品其他版本的默认状态
     * 
     * @param id - BOM ID
     * 
     * @returns Promise<Bom> 更新后的BOM（isDefault=true）
     * 
     * 数据流向：
     * 1. 用户在BOM列表点击"设为默认"按钮
     * 2. 前端调用：bomApi.setDefaultBom(123)
     * 3. 发送请求：POST /api/v1/boms/123/set-default
     * 4. 后端处理：BomController@setAsDefault
     *    - 调用 BomService::setAsDefault()
     *    - 取消同产品其他BOM的默认状态
     *    - 设置当前BOM为默认
     *    - 记录审计日志
     * 5. 返回更新后的BOM对象
     * 
     * 代码示例：
     * ```typescript
     * // 设置BOM为默认版本
     * const updatedBom = await bomApi.setDefaultBom(123)
     * console.log(updatedBom.isDefault)  // true
     * 
     * // 在列表组件中使用
     * async function handleSetDefault(bom: Bom) {
     *   try {
     *     await bomApi.setDefaultBom(bom.id)
     *     message.success('设置默认版本成功')
     *     await loadList()  // 刷新列表
     *   } catch (error) {
     *     message.error('设置失败：' + error.message)
     *   }
     * }
     * ```
     */
    setDefaultBom(id: number) {
      return client.post<Bom>(API_ROUTES.bom.setDefault(id))
    },

    /**
     * 通过BOM编码查找BOM
     * 
     * 使用场景：
     * - 扫码查询BOM
     * - 通过编码快速搜索
     * - API集成时根据编码获取BOM
     * 
     * @param code - BOM编码
     * 
     * @returns Promise<Bom> BOM详情（包含明细列表）
     * 
     * 数据流向：
     * 1. 用户扫描BOM编码或输入编码搜索
     * 2. 前端调用：bomApi.getBomByCode('BOM-2025-001')
     * 3. 发送请求：GET /api/v1/boms/code/BOM-2025-001
     * 4. 后端处理：BomController@findByCode
     *    - 调用 BomService::findByCode()
     *    - 查询数据库（根据bom_code字段）
     *    - 预加载关联数据（material, items）
     *    - 如果不存在，返回 404
     * 5. 返回BOM详情对象
     * 
     * 代码示例：
     * ```typescript
     * // 通过编码查询
     * const bom = await bomApi.getBomByCode('BOM-2025-001')
     * console.log(bom.bomName, bom.items)
     * 
     * // 扫码查询场景
     * async function handleScan(code: string) {
     *   try {
     *     const bom = await bomApi.getBomByCode(code)
     *     router.push(`/bom/${bom.id}`)  // 跳转到详情页
     *   } catch (error) {
     *     message.error('BOM不存在：' + code)
     *   }
     * }
     * ```
     */
    getBomByCode(code: string) {
      return client.get<Bom>(API_ROUTES.bom.findByCode(code))
    },

    /**
     * 获取BOM树形结构（所有BOM，无参数版本）
     * 
     * 使用场景：
     * - 在BOM管理首页显示完整的BOM树
     * - 可视化展示BOM层级关系
     * - 用于树形选择器
     * 
     * **注意**：此方法与 `getBomTree(id)` 不同
     * - `getBomTree(id)` - 获取单个BOM的子级树形结构（需要id参数）
     * - `getBomTreeAll()` - 获取所有顶层BOM的树形列表（无参数）
     * 
     * @param params - 可选查询参数
     *   - only_active?: boolean - 是否只显示生效的BOM
     *   - factory_id?: number - 按工厂筛选
     * 
     * @returns Promise<BomTreeNode[]> BOM树形数组
     *   返回数据结构：
     *   [
     *     {
     *       id: number,
     *       bomCode: string,
     *       bomName: string,
     *       materialId: number,
     *       materialCode: string,
     *       materialName: string,
     *       version: string,
     *       quantity: number,
     *       unit: string,
     *       isDefault: boolean,
     *       lifecycleStatus: string,
     *       status: number,
     *       children?: BomTreeNode[]  // 子BOM（递归结构）
     *     },
     *     // ... 更多顶层BOM
     *   ]
     * 
     * 数据流向：
     * 1. 用户打开BOM管理首页或树形选择器
     * 2. 前端调用：bomApi.getBomTreeAll({ only_active: true })
     * 3. 发送请求：GET /api/v1/boms/tree?only_active=true
     * 4. 后端处理：BomController@treeAll
     *    - 调用 BomService::getTree()
     *    - 查询所有顶层BOM（通过物料关联判断，没有其他BOM使用其物料）
     *    - 递归加载子BOM（通过明细表的material_id查找对应的BOM）
     *    - 构建树形结构
     * 5. 返回BOM树形数组
     * 
     * 代码示例：
     * ```typescript
     * // 获取所有BOM树
     * const tree = await bomApi.getBomTreeAll()
     * 
     * // 只显示生效的BOM
     * const activeTree = await bomApi.getBomTreeAll({ only_active: true })
     * 
     * // 按工厂筛选
     * const factoryTree = await bomApi.getBomTreeAll({ factory_id: 1 })
     * 
     * // 用于树形组件
     * <Tree data={tree} />
     * ```
     */
    getBomTreeAll(params?: BomQueryParams) {
      return client.get<BomTreeNode[]>(API_ROUTES.bom.tree, { params })
    },
  }
}
